import { defaultScreeningRecurrence } from './settings.seed.js';
import { initialComplianceSeeds } from './compliance.seed.js';
/**
 * The 126 requirement rules, derived from docs/compliance-catalog.md and reconciled
 * against docs/phase-1-decisions.md (issue #36).
 *
 * This is the executable requirement configuration, not an independent policy authority.
 * ADR-008 supersedes the original gate/recurrence limits after the September 12 review.
 * Disputed policy choices remain explicit until a versioned approval is recorded.
 *
 * Nothing here may be duplicated into feature code. `scripts/check-requirement-keys.mjs`
 * fails the build if any key below appears as a string literal in apps/web or apps/api.
 *
 * Reconciliation applied (see phase-1-decisions.md):
 *  - Decision 1, CHAP only: the two ACHC-keyed rows are dropped, and the CHAP side of each
 *    pick-one conflict is seeded.
 *  - Decision 5: 28 keys across the seven decided families gate `first-assignment`.
 *  - Decision 6: 24 agency-owned rows are visible, gate nothing, and count toward nobody's
 *    progress.
 *  - Decision 7: 21 rows are conditional and hidden until triggered.
 *  - Decision 8: `label` is the plain HR-facing name; `legalName` and `citations` are metadata.
 *  - Where the catalog never settled an interval, expiry is `unresolved` with its open-question
 *    id. Those rows show as open with NO due date rather than a guessed one.
 */
export const requirementSeeds = [
    ...initialComplianceSeeds,
    {
        key: 'internal-job-posting',
        label: 'Job posted internally',
        legalName: 'Internal position posting prior to filling',
        citations: ['HR 4.5.1', 'HR 4.6.1', 'CHAP HRM.1', 'ACHC HH4-2I.01'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'agency',
        expiry: {
            mode: 'none'
        },
        countsTowardProgress: false,
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.1 · line 64'
    },
    {
        key: 'employment-application',
        version: 2,
        label: 'Application or resume',
        legalName: 'Employment application and/or resume',
        citations: ['HR 4.8.1', 'CHAP HRM.1', 'ACHC HH4-2C.01'],
        gates: ['employment'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.1 · line 65'
    },
    {
        key: 'reference-checks',
        version: 3,
        label: 'Reference checks',
        legalName: 'Two completed pre-hire reference checks; educational or personal references accepted for no work experience',
        citations: [
            'HR 4.8.1 (two completed prehire checks)',
            'HR 4.5.1 (one reference)',
            'CHAP HRM.1'
        ],
        gates: ['employment'],
        compliance: { evidenceMode: 'references', minimumChecks: 2 },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.1 · line 66'
    },
    {
        key: 'work-history',
        version: 2,
        label: 'Work history',
        legalName: 'Employment history and documented verification, including an explicit no-prior-employment pathway',
        citations: ['HR 4.5.1', 'HR 4.8.1', 'HR 4.9.1', 'CHAP HRM.1'],
        gates: ['employment'],
        compliance: { evidenceMode: 'history-verification' },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.1 · line 67'
    },
    {
        key: 'credential-verification',
        // Education or license, not both (Julio, September 13, 2026; #147). Licensed, certified
        // and qualified roles are verified through their own credential rows and primary source
        // verification, so only profiles without one include this row (CHAP HRM.1/HRM.2 guidance).
        version: 2,
        label: 'Education and training verified',
        legalName: 'Verification of education and training',
        citations: ['HR 4.5.1', 'HR 4.7.1', 'CHAP HRM.2', 'HR 4.8.1', 'CHAP HRM.1', 'ACHC HH4-2C.01'],
        gates: ['employment'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 68'
    },
    {
        key: 'i9-form',
        version: 2,
        label: 'I-9 work authorization',
        legalName: 'Form I-9, Employment Eligibility Verification (IRCA 1986)',
        citations: ['HR 4.8.1', 'HR 4.9.1', 'HR 4.6.1', 'IRCA 1986'],
        trigger: { mode: 'conditional', clauses: [{ kind: 'employment-classification', anyOf: ['w2'] }, { kind: 'employer-responsibility', anyOf: ['vigeo'] }], showBeforeTrigger: true },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['employment'],
        fileCategory: 'segregated-i9',
        catalogRow: '§1.1 · line 69'
    },
    {
        key: 'criminal-background-check',
        version: 2,
        label: 'Background check',
        legalName: 'Criminal history background check',
        citations: ['HR 4.8.1', 'HR 4.9.1', 'CHAP HRM.1', 'ACHC HH4-2C.01'],
        kind: 'external-check',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['employment', 'independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 70'
    },
    {
        key: 'oig-leie-exclusion-check',
        version: 2,
        label: 'OIG exclusion check',
        legalName: 'OIG List of Excluded Individuals and Entities (LEIE) search',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC block)', 'LD 7.25.1', 'CHAP HRM.1'],
        gates: ['employment', 'independent-service'],
        kind: 'external-check',
        phase: 'both',
        owner: 'person',
        expiry: defaultScreeningRecurrence.find((row) => row.requirementKey === 'oig-leie-exclusion-check').expiry,
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 71'
    },
    {
        key: 'nsopw-sex-offender-check',
        version: 2,
        label: 'Sex offender registry check',
        legalName: 'National Sex Offender Public Website (NSOPW) search',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC block)', 'LD 7.25.1'],
        gates: ['employment', 'independent-service'],
        kind: 'external-check',
        phase: 'both',
        owner: 'person',
        expiry: defaultScreeningRecurrence.find((row) => row.requirementKey === 'nsopw-sex-offender-check').expiry,
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 72'
    },
    {
        key: 'conviction-review-determination',
        version: 2,
        label: 'Conviction review decision',
        legalName: 'Case-by-case employment determination where a conviction is found',
        citations: ['HR 4.6.1', 'CHAP HRM.1'],
        gates: ['independent-service'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'attribute',
                    attribute: 'has-conviction-disclosure',
                    anyOf: ['yes']
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.1 · line 73'
    },
    {
        key: 'job-description-signed',
        version: 2,
        label: 'Signed job description',
        legalName: 'Job description reviewed and signed by the employee',
        citations: [
            'HR 4.16.1',
            'HR 4.8.1',
            'HR 4.17.1 (acknowledgment clause on all 13 JDs)',
            'CHAP LG.8',
            'ACHC HH4-2E.01',
            'TJC HR.01.07.01'
        ],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        // The catalog says signed "at hire/orientation, and a new signed copy after each
        // revision" — so it is a one-time onboarding task that also recurs on change. It was
        // seeded as event-driven, which captured only the second half and made the row read
        // "Only if it happens — not an outstanding task" on the checklist. It GATES a first
        // assignment, so a reviewer was being told an item was optional while it blocked the
        // person. Re-signing on revision is catalog Q48 and needs a trigger, not this field.
        expiry: {
            mode: 'none'
        },
        gates: ['employment'],
        fileCategory: 'employee-records',
        waivable: true,
        catalogRow: '§1.1 · line 74'
    },
    {
        key: 'confidentiality-agreement',
        /*
         * BACK ON EVERY CORE CHECKLIST, 2026-09-30 (Paul, #372), AND BLOCKING AGAIN.
         *
         * It was off from 2026-09-13 (#147) on the belief that the signed HR 4.13A.3 Orientation
         * Checklist covered it at item 7. Paul: "HIPAA. Keep it as its own form. The Orientation
         * Checklist doesn't cover any of the portal steps." The key never changed, so evidence retained
         * while it was off is still attached to it.
         *
         * #147 removed THREE things here — `gates`, `waivable`, and the row's place in `set-core` — and
         * a first pass at this restored only the third. That put the row on all 14 checklists while it
         * withheld nothing: a row with no gates sorts into the "Required, not blocking" band, which
         * renders collapsed, and a journey probe reached Active with the agreement never collected. An
         * adversarial review of #397 demonstrated it.
         *
         * `waivable: false` BY PAUL'S DECISION, 2026-09-30, and NOT by reverting to what the row held
         * before #147 — it carried `waivable: true` then. Asked directly, because a HIPAA form
         * inheriting a waiver from a revert is not a decision anybody made:
         *
         *   "No exception. Treat it like the Orientation Checklist: it can't be waived and must be
         *    signed before a first assignment."
         *
         * So it now matches `orientation-completion`, which is pinned unwaivable for the same reason.
         *
         * This also closes a gap rather than merely changing a flag. `waivable: true` was INERT: a
         * granted exception carries the row's `employment` gate, every production caller derives
         * readiness at `first-assignment`, and `deriveReadiness` skips an exception whose gate differs
         * — so the app accepted a waiver, recorded it, and went on blocking. HR_Admin was offered a
         * control that did nothing. With `false` the control is not offered at all, which is both the
         * policy Paul asked for and an honest screen.
         *
         * The citations are deliberately NOT reverted. #147 also split
         * `'HR 4.8.1 and HR 4.9.1 (ACHC blocks)'` into two entries, which was a separate and correct
         * fix — this row does not sit behind an ACHC block — and a test pins it.
         */
        version: 2,
        label: 'Confidentiality agreement',
        legalName: 'Signed confidentiality agreement',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC block)', 'ACHC HH4-2C.01'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['employment'],
        fileCategory: 'employee-records',
        waivable: false,
        catalogRow: '§1.1 · line 75'
    },
    {
        key: 'conflict-of-interest',
        label: 'Conflict of interest disclosure',
        legalName: 'Conflict of interest disclosure statement',
        citations: ['HR 4.9.1 (ACHC block)', 'ACHC HH4-2C.01'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 76'
    },
    {
        key: 'employee-handbook-acknowledgment',
        /*
         * On no checklist since September 13, 2026 (#147), which held that the signed HR 4.13A.3
         * Orientation Checklist covers it at item 9. Kept for its stable key and retained evidence.
         *
         * THAT REASONING IS NOW IN DOUBT and this comment no longer states it as fact. Paul overturned
         * the identical claim for confidentiality on 2026-09-29 — "the Orientation Checklist doesn't
         * cover any of the portal steps" — and #372 carries the question for this row and the harassment
         * one. Neither is on the target portal-form list, so his sentence does not clearly reach them;
         * nobody has asked for them back, so nothing has moved.
         */
        version: 2,
        label: 'Handbook acknowledgment',
        legalName: 'Personnel policies / Employee Handbook acknowledgment',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC block)', 'ACHC HH4-2C.01'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.1 · line 77'
    },
    {
        key: 'harassment-policy-acknowledgment',
        /*
         * On no checklist since September 13, 2026 (#147), which held that the signed HR 4.13A.3
         * Orientation Checklist covers it at item 37. Kept for its stable key and retained evidence.
         *
         * IN DOUBT for the same reason as the handbook row above, and with a second reason of its own:
         * `docs/compliance-catalog.md` describes this acknowledgment as "typically captured inside the
         * handbook acknowledgment", which is not the orientation checklist either. #372 carries it.
         */
        version: 2,
        label: 'Harassment policy acknowledgment',
        legalName: 'Sexual and other harassment policy acknowledgment',
        citations: ['HR 4.3.1', 'CHAP HRM.1'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 78'
    },
    {
        key: 'withholding-statements',
        version: 2,
        label: 'Tax withholding forms',
        legalName: 'Dated and signed withholding statements',
        citations: ['HR 4.8.1', 'CHAP HRM.1'],
        trigger: { mode: 'conditional', clauses: [{ kind: 'employment-classification', anyOf: ['w2'] }, { kind: 'employer-responsibility', anyOf: ['vigeo'] }], showBeforeTrigger: true },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 79'
    },
    {
        key: 'wage-salary-record',
        label: 'Wage record',
        legalName: 'Salary recaps / wage record',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC "Wages")', 'ACHC HH4-2C.01'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 80'
    },
    {
        key: 'benefits-record',
        label: 'Benefits record',
        legalName: 'Employee benefits record',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC block)', 'HR 4.23.1'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 81'
    },
    {
        key: 'benefits-discussion-at-hire',
        label: 'Pay and benefits discussed at hire',
        legalName: 'Wages and benefits discussed at time of hire',
        citations: ['HR 4.22.1', 'HR 4.8.1', 'CHAP HRM.1'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 82'
    },
    {
        key: 'emergency-contact',
        label: 'Emergency contact',
        legalName: 'Designated emergency contact on file',
        citations: ['HR 4.2.1'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 83'
    },
    {
        key: 'change-of-status-forms',
        label: 'Change of status form',
        legalName: 'Change of status forms',
        citations: ['HR 4.8.1', 'CHAP HRM.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 84'
    },
    {
        key: 'attendance-record',
        label: 'Attendance record',
        legalName: 'Attendance and punctuality record',
        citations: ['HR 4.2.1', 'TJC HR.01.02.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.1 · line 85'
    },
    {
        key: 'employment-action-records',
        label: 'Employment action records',
        legalName: 'Employment, promotion, compensation and disciplinary action records',
        citations: ['HR 4.8.1', 'HR 4.2.1', 'CHAP HRM.7'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.1 · line 86'
    },
    {
        key: 'negative-outcomes-record',
        label: 'Negative outcomes on file',
        legalName: 'Record of negative outcomes (term undefined in the source policies)',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC block)', 'ACHC HH4-2C.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.1 · line 87'
    },
    {
        key: 'separation-record',
        label: 'Separation paperwork',
        legalName: 'Termination / separation documentation',
        citations: ['HR 4.8.1', 'CHAP HRM.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'offboarding-separations',
        catalogRow: '§1.1 · line 88'
    },
    {
        key: 'personnel-record-retention',
        label: 'Personnel file retention',
        legalName: 'Retention of personnel records for a minimum of five years after separation',
        citations: ['HR 4.8.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'none'
        },
        countsTowardProgress: false,
        fileCategory: 'offboarding-separations',
        catalogRow: '§1.1 · line 89'
    },
    {
        key: 'personnel-file-access-control',
        label: 'Personnel file access limits',
        legalName: 'Access to personnel files restricted to authorized staff',
        citations: ['HR 4.8.1', 'CHAP HRM.1', 'TJC HR.01.02.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'none'
        },
        countsTowardProgress: false,
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 90'
    },
    {
        key: 'employee-file-review-supervised',
        label: 'Supervised file review',
        legalName: 'Employee reviews own personnel file only with administrative staff present',
        citations: ['HR 4.8.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'event-driven'
        },
        countsTowardProgress: false,
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 91'
    },
    {
        key: 'personnel-info-release-authorization',
        label: 'Release authorization',
        legalName: 'Written authorization before releasing personnel information',
        citations: ['HR 4.8.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'event-driven'
        },
        countsTowardProgress: false,
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 92'
    },
    {
        key: 'health-file-segregation',
        label: 'Health records kept separate',
        legalName: 'Employee health information held separately from the personnel file',
        citations: ['HR 4.8.1', 'ADA', 'OSHA 1910.1030'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'none'
        },
        countsTowardProgress: false,
        // Files to policies-compliance, NOT segregated-health. This row is the agency's
        // CONTROL — evidence that health information is held separately and that the practice
        // is verified at each file audit. It is not anybody's health record, and filing an
        // agency control behind the narrowest access list in the tenant would both hide it
        // from the auditors who need it and imply it is somebody's medical file. The source
        // catalog's "SEGREGATED health file" named the control's subject, not its destination.
        fileCategory: 'policies-compliance',
        catalogRow: '§1.1 · line 93'
    },
    {
        key: 'contract-written-agreement',
        label: 'Signed contractor agreement',
        legalName: 'Signed written agreement with contractual staff',
        citations: ['HR 4.9.1', 'CHAP HRM.22', 'TJC LD.04.03.09', 'CoP 484.105(e)'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 94'
    },
    {
        key: 'contract-staff-file-accessibility',
        label: 'Contractor file on hand',
        legalName: 'Contractor personnel file maintained by or accessible to the Agency',
        citations: ['HR 4.9.1', 'CHAP HRM.1, HRM.22', 'ACHC HH4-2B.01'],
        kind: 'document',
        phase: 'both',
        owner: 'agency',
        expiry: {
            mode: 'event-driven'
        },
        countsTowardProgress: false,
        fileCategory: 'employee-records',
        catalogRow: '§1.1 · line 95'
    },
    {
        key: 'rn-license',
        version: 2,
        label: 'RN license',
        legalName: 'Current Registered Nurse license, state board, in good standing',
        citations: ['HR 4.7.1', 'HR 4.6.1', 'HR 4.17I.23', 'CHAP HRM.2, HRM.3', 'CoP 484.75(b)'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 101'
    },
    {
        key: 'lpn-license',
        version: 2,
        label: 'LPN/LVN license',
        legalName: 'Current Licensed Practical / Vocational Nurse license, state board',
        citations: ['HR 4.7.1', 'HR 4.17K.27', 'CHAP HRM.2, HRM.3'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 102'
    },
    {
        key: 'pt-license',
        version: 2,
        label: 'PT license',
        legalName: 'Current Registered Physical Therapist license, state board',
        citations: ['HR 4.7.1', 'HR 4.17O.35', 'CHAP HRM.2, HRM.3'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 103'
    },
    {
        key: 'pta-license',
        version: 2,
        label: 'PTA license',
        legalName: 'Current Physical Therapist Assistant license, registration or certification',
        citations: ['HR 4.7.1', 'HR 4.17Q.39', 'CHAP HRM.2, HRM.3'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 104'
    },
    {
        key: 'ot-license',
        version: 2,
        label: 'OT license',
        legalName: 'Current Occupational Therapist license',
        citations: ['HR 4.7.1', 'HR 4.17W.51', 'CHAP HRM.2, HRM.3'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 105'
    },
    {
        key: 'cota-certification',
        version: 2,
        label: 'COTA certification',
        legalName: 'NBCOT certification maintained plus current state certification or licensure',
        citations: ['HR 4.7.1', 'HR 4.17Y.55', 'CHAP HRM.2, HRM.3'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 106'
    },
    {
        key: 'slp-license',
        version: 2,
        label: 'SLP license',
        legalName: 'Current Speech-Language Pathologist license',
        citations: ['HR 4.7.1', 'HR 4.17U.47', 'CHAP HRM.2, HRM.3'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 107'
    },
    {
        key: 'msw-degree',
        label: 'Social work degree',
        legalName: 'Accredited master\'s or doctoral social work degree plus one year of health-care social work experience',
        citations: ['HR 4.17S.43', 'HR 4.16.1'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 108'
    },
    {
        key: 'msw-license',
        version: 2,
        label: 'Social work license',
        legalName: 'Current social work license where required by state law',
        citations: ['HR 4.7.1', 'CHAP HRM.2'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 109'
    },
    {
        key: 'hha-qualification-pathway',
        version: 2,
        label: 'Aide qualification',
        legalName: 'Home health aide qualification: 75-hour training program, state program plus registry listing, or state licensure',
        citations: ['HR 4.12.1', 'HR 4.17M.31', 'CoP 484.80(a)(b)', 'CHAP HRM.9, HRM.10, HRM.12'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 111'
    },
    {
        key: 'hha-state-registry-good-standing',
        version: 2,
        label: 'Aide registry standing',
        legalName: 'Home health aide currently listed in good standing on the state nurse aide registry',
        citations: ['HR 4.12.1', 'CoP 484.80(a)'],
        kind: 'external-check',
        phase: 'both',
        owner: 'person',
        // Q27 ANSWERED 2026-08-30 by Julio: re-verify annually. Before this the row was
        // `unresolved`, and because it also gates a first assignment, every registry-qualified
        // aide sat permanently at "Needs review" and could not be cleared to start.
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [60, 30, 14, 7, 1]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'attribute',
                    attribute: 'qualified-via-aide-registry',
                    anyOf: ['yes']
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.2 · line 112'
    },
    {
        key: 'hha-24-month-lapse-rule',
        label: '24-month service lapse',
        legalName: 'Twenty-four-month lapse in compensated service voids home health aide program completion',
        citations: ['HR 4.12.1', 'CoP 484.80(a)(3)'],
        kind: 'external-check',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'rolling-window',
            windowDays: 730,
            target: 1,
            unit: 'occurrences',
            paceWarningRatio: 0.5
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 113'
    },
    {
        key: 'administrator-qualification',
        label: 'Administrator qualifications',
        legalName: 'Administrator qualification',
        citations: ['HR 4.17A.2', 'HR 4.16.1'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 114'
    },
    {
        key: 'dcs-qualification',
        version: 2,
        label: 'DCS nursing registration',
        legalName: 'Director of Clinical Services registered in the state(s) where practicing',
        citations: ['HR 4.17C.7', 'HR 4.7.1'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 115'
    },
    {
        key: 'clinical-manager-qualification',
        version: 2,
        label: 'Clinical Manager credential',
        legalName: 'Clinical Manager: approved nursing or therapy school graduate holding a current license',
        citations: ['HR 4.17G.17', 'HR 4.7.1'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 116'
    },
    {
        key: 'qapi-coordinator-qualification',
        label: 'QAPI Coordinator credential',
        legalName: 'QAPI Coordinator: current nursing license or qualified health care professional status',
        citations: ['HR 4.17E.13', 'HR 4.7.1'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'unresolved',
            openQuestionId: 'Q53'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 117'
    },
    {
        key: 'specialty-certification',
        label: 'Specialty certification',
        legalName: 'Specialty-practice certification where the role or assignment requires it',
        citations: ['HR 4.8.1', 'CHAP HRM.2'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'employee-records',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'specialty-practice-assignment',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.2 · line 118'
    },
    {
        key: 'license-copy-at-hire',
        version: 2,
        label: 'License copy at hire',
        legalName: 'Copy of current license, certification, diploma and/or registration collected at hire',
        citations: ['HR 4.7.1 procedure 2', 'CHAP HRM.2'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.2 · line 119'
    },
    {
        key: 'license-attestation-letter',
        label: 'Attestation letter',
        legalName: 'Letter of attestation from the licensing or accrediting institution',
        citations: ['HR 4.7.1 procedure 2'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'recruitment-onboarding',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'requirement-status',
                    requirementKey: 'license-copy-at-hire',
                    anyOf: ['rejected']
                }
            ],
            showBeforeTrigger: true
        },
        catalogRow: '§1.2 · line 120'
    },
    {
        key: 'psv-at-hire',
        version: 2,
        label: 'Primary source check at hire',
        legalName: 'Primary source verification with the state board at time of hire',
        citations: [
            'HR 4.7.1 procedures 3 and 5',
            'CHAP HRM.2, HRM.3',
            'TJC HR.01.02.01',
            'CoP 484.75(b)'
        ],
        kind: 'external-check',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 121'
    },
    {
        key: 'psv-at-renewal',
        label: 'Primary source check at renewal',
        legalName: 'Primary source verification at each credential renewal',
        citations: ['HR 4.7.1 procedures 3 and 5', 'CHAP HRM.3', 'TJC HR.01.02.05'],
        kind: 'external-check',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 122'
    },
    {
        key: 'psv-verification-record-retained',
        label: 'Verification record kept',
        legalName: 'Documented record of every primary source verification',
        citations: ['HR 4.7.1 procedure 5', 'CHAP HRM.3', 'TJC HR.01.02.05'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 123'
    },
    {
        key: 'supervisor-credential-notification',
        label: 'Supervisor told of credentials',
        legalName: 'Supervisor provided with current licensure and certification information',
        citations: ['HR 4.7.1 procedure 1'],
        kind: 'document',
        phase: 'both',
        owner: 'agency',
        expiry: {
            mode: 'event-driven'
        },
        countsTowardProgress: false,
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 124'
    },
    {
        key: 'license-lapse-work-stoppage',
        label: 'Stop work on lapsed license',
        legalName: 'Work stoppage on a lapsed license or certification',
        citations: ['HR 4.7.1 procedure 6'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.2 · line 125'
    },
    {
        key: 'cpr-certification',
        version: 2,
        label: 'CPR card',
        legalName: 'CPR / BLS certification',
        citations: ['HR 4.8.1', 'HR 4.9.1'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'training-competency',
        catalogRow: '§1.2 · line 126'
    },
    {
        key: 'cpr-online-inperson-verification',
        version: 2,
        label: 'In-person CPR check',
        legalName: 'In-person verification of competency accompanying an online CPR card',
        citations: ['HR 4.8.1'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        gates: ['independent-service'],
        fileCategory: 'training-competency',
        catalogRow: '§1.2 · line 127'
    },
    {
        key: 'drivers-license',
        version: 2,
        label: 'Driver\'s license',
        legalName: 'Current state driver\'s license',
        citations: ['HR 4.20.1 procedure 1', 'HR 4.8.1', 'HR 4.9.1', 'CHAP HRM.1', 'ACHC HH4-1B.01'],
        gates: ['driving'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 128'
    },
    {
        key: 'motor-vehicle-record-check',
        version: 3,
        label: 'Driving record check',
        legalName: 'Motor vehicle record check through the state DMV',
        citations: ['HR 4.20.1', 'HR 4.8.1 (patient transport)'],
        trigger: { mode: 'conditional', clauses: [{ kind: 'attribute', attribute: 'transports-patients', anyOf: ['yes'] }], showBeforeTrigger: true },
        gates: ['driving'],
        kind: 'external-check',
        phase: 'both',
        owner: 'person',
        expiry: { mode: 'calendar-months', everyMonths: 12, anchor: 'last-completion' },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 129'
    },
    {
        key: 'personal-vehicle-insurance',
        version: 2,
        label: 'Personal auto insurance',
        legalName: 'Personal vehicle insurance for employees driving on Agency business',
        citations: ['HR 4.21.1 procedure 2', 'CHAP HRM.1', 'ACHC HH4-2I.01'],
        gates: ['driving'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 130'
    },
    {
        key: 'agency-vehicle-insurance',
        label: 'Agency vehicle insurance',
        legalName: 'Insurance on each Agency-owned vehicle',
        citations: ['HR 4.21.1 procedure 1', 'ACHC HH4-2I.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 131'
    },
    {
        key: 'contract-staff-licensed-before-service',
        version: 2,
        label: 'Contractor licensed before first visit',
        legalName: 'Contractual staff licensed or certified before any patient service',
        citations: ['HR 4.9.1', 'HR 4.7.1', 'CHAP HRM.2', 'CoP 484.75(b)'],
        kind: 'credential',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'employee-records',
        catalogRow: '§1.2 · line 132'
    },
    {
        key: 'orientation-completion',
        version: 3,
        label: 'Orientation complete',
        legalName: 'Agency orientation completed and documented',
        citations: [
            'HR 4.13.1',
            'HR 4.13A.3',
            'HR 4.8.1',
            'HR 4.9.1',
            'CoP 484.75(b)',
            'ACHC HH4-4A.01, HH4-5A.01'
        ],
        compliance: { evidenceMode: 'orientation', documents: [{ label: 'Open HR4.13A.3 orientation checklist', url: 'https://vigeohealth2.sharepoint.com/sites/ComplianceDepartment/NEW%20Policies%20%20Procedures/Home%20Health%20Policies%20%26%20Procedures/HR_4.1.1_Human%20Resources/HR_4.13A.3_Orientation_Checklist.docx' }], instructions: 'Use HR4.13A.3: 41 topics plus Other, actual applicable Yes/N/A responses and employee/supervisor signatures and dates. Retain role-specific competency separately. Signing it does not stand in for the portal forms: confidentiality and HIPAA (item 7) need their own signed row. The handbook (item 9) and the Sexual Harassment Act (item 37) have no separate row today.' },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.3 · line 138'
    },
    {
        key: 'orientation-job-specific-and-equipment-training',
        version: 2,
        label: 'Job and equipment orientation',
        legalName: 'Job-specific and equipment orientation, including special populations',
        citations: ['HR 4.13.1 (ACHC block)', 'HR 4.13A.3 items 12, 15'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'recruitment-onboarding',
        catalogRow: '§1.3 · line 139'
    },
    {
        key: 'new-hire-close-supervision',
        version: 2,
        label: 'New hire supervision',
        legalName: 'Closer monitoring and supervision of newly hired staff',
        citations: ['HR 4.6.1', 'CHAP HRM.5', 'CoP 484.75(b)'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: { mode: 'event-driven' },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 140'
    },
    {
        key: 'initial-competency-before-independent-care',
        version: 2,
        label: 'Initial competency',
        legalName: 'Initial competency evaluation during orientation, before independent care',
        citations: [
            'HR 4.15.1',
            'CHAP HRM.11',
            'CoP 484.75(b), 484.80(b)(c)',
            'TJC HR.01.06.01',
            'ACHC HH4-6A.01'
        ],
        gates: ['independent-service'],
        compliance: { evidenceMode: 'reviewed' },
        trigger: { mode: 'conditional', clauses: [{ kind: 'attribute', attribute: 'provides-patient-services', anyOf: ['yes'] }], showBeforeTrigger: true },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 141'
    },
    {
        key: 'competency-evaluator-qualification',
        version: 2,
        label: 'Evaluator qualified',
        legalName: 'Competency evaluator meets the discipline-specific pairing',
        citations: ['HR 4.15.1', 'HR 4.12.1', 'CoP 484.80(c)', 'CHAP HRM.11'],
        gates: ['independent-service'],
        compliance: { evidenceMode: 'reviewed' },
        kind: 'document',
        phase: 'both',
        owner: 'agency',
        expiry: {
            mode: 'none'
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 142'
    },
    {
        key: 'ongoing-competency-triennial',
        version: 2,
        label: 'Annual competency review',
        legalName: 'Ongoing competency assessment annually for all patient care staff',
        citations: ['HR 4.15.1 (policy body)', '12VAC5-381-180 E4', 'CHAP HRM.11', 'CoP 484.80(b)(c)'],
        gates: ['independent-service'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: { mode: 'calendar-months', everyMonths: 12, anchor: 'last-completion' },
        compliance: { evidenceMode: 'reviewed', firstReviewMonths: 12, reviewOwner: 'HR and clinical supervisor' },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 143'
    },
    {
        key: 'competency-on-new-procedure-or-equipment',
        label: 'New equipment competency',
        legalName: 'Competency reassessment when new procedures, techniques or equipment are introduced',
        citations: ['HR 4.15.1', 'HR 4.12.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'training-competency',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'new-procedure-or-equipment-introduction',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.3 · line 145'
    },
    {
        key: 'hha-initial-onsite-competency',
        version: 2,
        label: 'Aide initial competency',
        legalName: 'Home health aide initial on-site competency evaluation by an RN before furnishing services',
        citations: ['HR 4.12.1', 'HR 4.15.1', 'CoP 484.80(c)', 'CHAP HRM.11, HRM.12'],
        gates: ['independent-service'],
        compliance: { evidenceMode: 'reviewed' },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 146'
    },
    {
        key: 'hha-annual-onsite-competency',
        version: 2,
        label: 'Aide annual competency',
        legalName: 'Home health aide on-site competency assessment every 12 months, in the patient home during care',
        citations: ['HR 4.11.1', 'HR 4.15.1', 'HR 4.15O.20', 'HR 4.8.1', 'CoP 484.80(c)', '12VAC5-381-180 E4', 'CHAP HRM.11'],
        gates: ['independent-service'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: { mode: 'calendar-months', everyMonths: 12, anchor: 'last-completion' },
        compliance: { evidenceMode: 'reviewed', firstReviewMonths: 12, reviewOwner: 'HR and clinical supervisor' },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 147'
    },
    {
        key: 'hha-unsatisfactory-task-remediation',
        label: 'Aide task retraining',
        legalName: 'Per-task restriction, retraining and re-evaluation after an unsatisfactory rating',
        citations: ['HR 4.12.1', 'CoP 484.80(c)'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'training-competency',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'unsatisfactory-competency-task-rating',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.3 · line 148'
    },
    {
        key: 'hha-trainer-rn-experience-qualification',
        label: 'Aide trainer experience',
        legalName: 'RN who trains home health aides holds two years of nursing experience, at least one in home health',
        citations: ['HR 4.12.1', 'CoP 484.80(a)', 'CHAP HRM.9, HRM.10'],
        kind: 'credential',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'employee-records',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'attribute',
                    attribute: 'designated-aide-trainer',
                    anyOf: ['yes']
                }
            ],
            showBeforeTrigger: false,
            explanation: 'Only RNs designated as aide trainers (catalog Q14).'
        },
        catalogRow: '§1.3 · line 149'
    },
    {
        key: 'inservice-education-record',
        label: 'In-service record',
        legalName: 'Inservice education record maintained',
        citations: ['HR 4.8.1', 'HR 4.9.1', 'HR 4.23.1', 'IC 5.12.1', 'CHAP HRM.5'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 150'
    },
    {
        key: 'inservice-hours-direct-care',
        label: '12 in-service hours',
        legalName: 'Twelve hours of ongoing education per 12-month period, direct care personnel',
        citations: [
            'HR 4.14.1 (ACHC block)',
            'CHAP HRM.8',
            'CoP 484.75(b), 484.80(d)',
            'ACHC HH4-8A.01'
        ],
        kind: 'hours-accumulator',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'rolling-window',
            windowDays: 365,
            target: 12,
            unit: 'hours',
            paceWarningRatio: 0.5
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 151'
    },
    {
        key: 'inservice-hours-non-direct-care',
        label: '8 in-service hours',
        legalName: 'Eight hours of ongoing education per year, non-direct care personnel',
        citations: ['HR 4.14.1 (ACHC block)', 'ACHC HH4-8A.01'],
        kind: 'hours-accumulator',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'rolling-window',
            windowDays: 365,
            target: 8,
            unit: 'hours',
            paceWarningRatio: 0.5
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 152'
    },
    {
        key: 'hha-inservice-12-hours-rn-supervised',
        label: 'Aide 12 in-service hours',
        legalName: 'Home health aide twelve hours of in-service per 12-month period, RN-supervised',
        citations: ['HR 4.14.1', 'HR 4.12.1', 'CoP 484.80(d)', 'CHAP HRM.13, HRM.14'],
        kind: 'hours-accumulator',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'rolling-window',
            windowDays: 365,
            target: 12,
            unit: 'hours',
            paceWarningRatio: 0.5
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 153'
    },
    {
        key: 'annual-education-plan-topics',
        label: 'Annual education plan',
        legalName: 'Annual education plan covering the nine mandated topics',
        citations: ['HR 4.14.1 (ACHC block)', 'ACHC HH4-8A.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'none'
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 154'
    },
    {
        key: 'bloodborne-pathogens-training',
        label: 'Bloodborne pathogens training',
        legalName: 'OSHA bloodborne pathogens training',
        citations: ['IC 5.12.1', 'HR 4.14.1', 'HR 4.13A.3 item 18', 'CHAP IPC.2, IPC.15', 'OSHA 1910.1030'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 155'
    },
    {
        key: 'tb-education-training',
        label: 'TB education',
        legalName: 'Tuberculosis education',
        citations: ['IC 5.15.1', 'HR 4.14.1', 'HR 4.13A.3 item 16', 'CHAP IPC.14'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 156'
    },
    {
        key: 'hazmat-right-to-know-training',
        label: 'Hazmat / SDS training',
        legalName: 'Hazardous materials and Right-to-Know (SDS) training',
        citations: ['HR 4.14.1', 'HR 4.13A.3 item 17', 'ACHC HH4-8A.01'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 157'
    },
    {
        key: 'emergency-operations-plan-training',
        version: 2,
        label: 'Emergency plan training',
        legalName: 'Emergency Operations Plan education including the employee\'s role',
        citations: ['EM 2.4.1', 'CHAP EP.7', 'HR 4.13A.3 item 14'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: { mode: 'calendar-months', everyMonths: 24, anchor: 'last-completion' },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 158'
    },
    {
        key: 'infection-control-training',
        label: 'Infection control training',
        legalName: 'General infection prevention and control training',
        citations: ['IC 5.4A.5', 'IC 5.5.1', 'CHAP IPC.2, IPC.13'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 159'
    },
    {
        key: 'workplace-violence-prevention-training',
        version: 2,
        label: 'Workplace violence training',
        legalName: 'Workplace violence prevention education',
        citations: ['HR 4.14.1 (citing CDC/NIOSH, Feb 2012)', 'CHAP HRM.8'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: { mode: 'event-driven' },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 160'
    },
    {
        key: 'safety-roles-responsibilities-training',
        version: 2,
        label: 'Safety responsibilities training',
        legalName: 'Roles and responsibilities related to safety',
        citations: ['HR 4.19.1', 'CHAP HRM.1', 'CoP 484.75(b)'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: { mode: 'event-driven' },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 161'
    },
    {
        key: 'staff-personal-safety-education',
        version: 2,
        label: 'Personal safety training',
        legalName: 'Staff personal safety education',
        citations: ['EC 1.2.1', 'CHAP HRM.1'],
        kind: 'recurring-event',
        phase: 'both',
        owner: 'person',
        expiry: { mode: 'event-driven' },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 162'
    },
    {
        key: 'ceu-validation-clinical-staff',
        label: 'CEUs verified',
        legalName: 'Continuing education units validated where state law requires',
        citations: ['HR 4.14.1'],
        kind: 'external-check',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'credential-date',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.3 · line 163'
    },
    {
        key: 'hha-supervisory-visit-14-day-skilled',
        label: 'Aide visit check (14 days)',
        legalName: 'On-site supervisory visit every 14 days where the patient also receives skilled services',
        citations: [
            'HR 4.11.1',
            'HR 4.17.1 (RN, PT, SLP instruments)',
            'CoP 484.80(h)',
            'CHAP CDT.14–CDT.17, HRM.23–HRM.25'
        ],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 14,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                7,
                3,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.4 · line 169'
    },
    {
        key: 'hha-virtual-supervision-cap',
        label: 'Virtual supervision limit',
        legalName: 'Virtual supervisory assessments capped at one per 60-day period',
        citations: ['HR 4.11.1', 'CoP 484.80(h)', 'CMS-1747-F'],
        kind: 'hours-accumulator',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'unresolved',
            openQuestionId: 'Q32'
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.4 · line 170'
    },
    {
        key: 'hha-supervisory-visit-60-day-nonskilled',
        label: 'Aide visit check (60 days)',
        legalName: 'On-site supervisory visit every 60 days where the patient receives no skilled service, RN only',
        citations: ['HR 4.11.1', 'CoP 484.80(h)', 'CHAP CDT.14–CDT.17'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 60,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.4 · line 171'
    },
    {
        key: 'hha-semiannual-direct-observation-nonskilled',
        label: 'Aide observation (6 months)',
        legalName: 'Direct observation of each aide every six months where patients receive no skilled service, RN only',
        citations: ['HR 4.11.1', 'CoP 484.80(h)'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 182,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.4 · line 172'
    },
    {
        key: 'hha-concern-triggered-supervisory-visit',
        label: 'Aide visit after a concern',
        legalName: 'On-site observation visit whenever an area of concern in aide services is noted',
        citations: ['HR 4.11.1', 'CoP 484.80(h)', 'CHAP HRM.23'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'event-driven'
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'aide-service-concern',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.4 · line 173'
    },
    {
        key: 'hha-deficiency-retraining-and-competency-eval',
        label: 'Aide retraining after deficiency',
        legalName: 'Retraining plus targeted competency evaluation after a verified aide deficiency',
        citations: ['HR 4.11.1', 'CoP 484.80(h)', 'CHAP HRM.24'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'training-competency',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'verified-aide-deficiency',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.4 · line 174'
    },
    {
        key: 'contract-hha-supervision-and-competency',
        label: 'Contracted aide oversight',
        legalName: 'Contracted aide services: Agency retains supervision and competency verification duty',
        citations: ['HR 4.11.1', 'CoP 484.80(h)', 'CHAP HRM.25'],
        kind: 'external-check',
        phase: 'both',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.4 · line 175'
    },
    {
        key: 'pta-ota-supervisory-visit',
        label: 'Assistant supervision visit',
        legalName: 'PTA/OTA supervisory visit every 30 days, every 12th visit, or per state law, whichever occurs first',
        citations: [
            'HR 4.10.1',
            'HR 4.17P.37',
            'CoP 484.75(c), 484.100, 484.115',
            'CHAP CDT.8, HRM.18, HRM.20'
        ],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 30,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                14,
                7,
                3,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.4 · line 176'
    },
    {
        key: 'pta-ota-supervising-therapist-assignment',
        label: 'Supervising therapist named',
        legalName: 'Documented qualified supervising therapist for each PTA/OTA',
        citations: ['HR 4.10.1', 'CoP 484.75(c), 484.115', 'CHAP CDT.8, HRM.18'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'employee-records',
        catalogRow: '§1.4 · line 177'
    },
    {
        key: 'performance-appraisal',
        version: 3,
        label: 'Annual performance and competency review',
        legalName: 'Performance appraisal against the job description, annually',
        citations: [
            'HR 4.17.1 (policy body)',
            'TJC HR.01.07.01',
            'CHAP HRM.22, LG.8',
            'CoP 484.75(b)(c)'
        ],
        trigger: { mode: 'conditional', clauses: [{ kind: 'employment-classification', anyOf: ['w2'] }], showBeforeTrigger: true },
        gates: ['employment'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: { mode: 'calendar-months', everyMonths: 12, anchor: 'last-completion' },
        compliance: { evidenceMode: 'reviewed', instructions: 'Annual employee performance and competency review using the applicable role-specific assessment, qualified evaluator and retained signed evidence.', firstReviewMonths: 12, reviewOwner: 'HR and clinical supervisor' },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.4 · line 178'
    },
    {
        key: 'performance-appraisal-hha-annual',
        version: 2,
        label: 'Aide performance review',
        legalName: 'Home health aide performance appraisal every 12 months',
        citations: ['HR 4.17.1 (HHA carve-out)', 'HR 4.12.1', 'CoP 484.80(c)', 'CHAP HRM.22'],
        gates: ['independent-service'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: { mode: 'calendar-months', everyMonths: 12, anchor: 'last-completion' },
        compliance: { evidenceMode: 'reviewed', firstReviewMonths: 12, reviewOwner: 'HR and clinical supervisor' },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.4 · line 180'
    },
    {
        key: 'annual-job-duties-evaluation',
        label: 'Job duties review',
        legalName: 'Annual evaluation of job duties',
        citations: ['HR 4.8.1', 'HR 4.9.1 (ACHC block)', 'ACHC HH4-2C.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.4 · line 181'
    },
    {
        key: 'performance-appraisal-role-specific-instrument',
        label: 'Correct appraisal form',
        legalName: 'Correct role-specific appraisal instrument used',
        citations: ['HR 4.17.1', 'CHAP HRM.22'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.4 · line 182'
    },
    {
        key: 'performance-appraisal-dual-signature',
        label: 'Appraisal signed by both',
        legalName: 'Appraisal signed and dated by both employee and supervisor',
        citations: ['HR 4.17.1', 'CHAP HRM.22', 'ACHC HH4-13.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.4 · line 183'
    },
    {
        key: 'unsatisfactory-performance-investigation-remediation',
        label: 'Performance issue follow-up',
        legalName: 'Investigation and remediation of reported unsatisfactory performance',
        citations: ['HR 4.17.1', 'CoP 484.75(b)(c), 484.80(c)', 'CHAP HRM.22', 'ACHC HH4-2J.01'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'performance-employee-relations',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'reported-unsatisfactory-performance',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.4 · line 184'
    },
    {
        key: 'informal-ongoing-performance-review',
        version: 2,
        label: 'Ongoing supervision check-ins',
        legalName: 'Ongoing informal performance review through supervisory interaction',
        citations: ['HR 4.17.1', 'CHAP HRM.22'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: { mode: 'event-driven' },
        fileCategory: 'performance-employee-relations',
        catalogRow: '§1.4 · line 185'
    },
    {
        key: 'disciplinary-probation-period',
        label: 'Disciplinary probation',
        legalName: 'Disciplinary probation of 30, 60 or 90 days imposed by the Director of Clinical Services',
        citations: ['HR 4.24.1', 'TJC HR.01.02.01', 'CHAP HRM.1'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'performance-employee-relations',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'disciplinary-action',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.4 · line 186'
    },
    {
        key: 'visit-verification-re-education-record',
        label: 'Visit signature re-education',
        legalName: 'Re-education or corrective action for repeated missing visit signatures',
        citations: ['HR 4.26A.2'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'performance-employee-relations',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'repeated-missing-visit-signatures',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.4 · line 187'
    },
    {
        key: 'pre-employment-physical',
        version: 3,
        label: 'Clinically required fitness review',
        legalName: 'Applicable existing clinical findings and restrictions; no universal physical examination',
        citations: [
            'IC 5.15.1 (indirect)',
            'HR 4.8.1 ("any other Agency required health requirements")'
        ],
        trigger: { mode: 'conditional', clauses: [{ kind: 'attribute', attribute: 'requires-fitness-for-duty', anyOf: ['yes'] }], showBeforeTrigger: true },
        compliance: { evidenceMode: 'reviewed' },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 193'
    },
    {
        key: 'tb-baseline-risk-assessment',
        version: 3,
        label: 'TB512 or accepted PCP TB documentation',
        legalName: 'TB512 November 2021 or accepted PCP documentation, including screener findings and follow-up',
        citations: ['CHAP IPC.14', 'IC 5.15.1', 'HR 4.8.1'],
        compliance: { evidenceMode: 'reviewed', documents: [{ label: 'Download VIGEO TB512 (November 2021)', url: '/forms/VIGEO-TB512-November-2021.pdf' }], instructions: 'Use TB512 or acceptable PCP TB documentation. The qualified screener records findings, actions and follow-up. Retain the completed document in the restricted health record.' },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 194'
    },
    {
        key: 'tb-symptom-evaluation',
        version: 2,
        label: 'TB symptom check',
        legalName: 'TB symptom evaluation',
        citations: ['CHAP IPC.14', 'IC 5.15.1'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 195'
    },
    {
        key: 'tb-test-baseline',
        version: 2,
        label: 'TB test',
        legalName: 'Baseline TB test (blood test or skin test; two-step where required)',
        citations: ['CHAP IPC.14', 'IC 5.15.1'],
        kind: 'credential',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        gates: ['independent-service'],
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 196'
    },
    {
        key: 'tb-periodic-retest',
        version: 2,
        label: 'TB retest',
        legalName: 'Periodic TB retest',
        citations: ['IC 5.15.1', 'CHAP IPC.14'],
        kind: 'credential',
        phase: 'continuous',
        owner: 'person',
        expiry: { mode: 'event-driven' },
        trigger: { mode: 'conditional', clauses: [{ kind: 'record-exists', recordType: 'tb-retest-required', expected: true }], showBeforeTrigger: false },
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 197'
    },
    {
        key: 'tb-positive-followup',
        label: 'TB follow-up',
        legalName: 'TB follow-up on a positive test or conversion',
        citations: ['CHAP IPC.14', 'IC 5.15.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'segregated-health',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'record-exists',
                    recordType: 'tb-positive-result',
                    expected: true
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.5 · line 198'
    },
    {
        key: 'annual-tb-risk-assessment-agency',
        label: 'Agency TB risk assessment',
        legalName: 'Annual agency TB Risk Assessment plus PPD aggregate database',
        citations: ['IC 5.15.1', 'IC 5.15A.9', 'CHAP IPC.14'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.5 · line 199'
    },
    {
        key: 'hepb-info-sheet-issued',
        version: 2,
        label: 'Hep B info sheet',
        legalName: 'Hepatitis B vaccine Employee Information Sheet issued',
        citations: ['IC 5.12.1', 'CHAP IPC.15', 'OSHA 1910.1030'],
        compliance: { evidenceMode: 'education', instructions: 'Provide current Hepatitis B education for occupational exposure. Do not reuse superseded clinical instructions from the legacy attachment.' },
        trigger: { mode: 'conditional', clauses: [{ kind: 'attribute', attribute: 'occupational-blood-exposure', anyOf: ['yes'] }], showBeforeTrigger: true },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.5 · line 200'
    },
    {
        key: 'hepb-offer-and-response',
        version: 2,
        label: 'Hep B vaccine offer',
        legalName: 'Hepatitis B vaccination offered, and the employee\'s response recorded',
        citations: ['CHAP IPC.15', 'IC 5.12.1', 'HR 4.8.1', 'OSHA 1910.1030'],
        compliance: { evidenceMode: 'hepatitis-b', instructions: 'For occupational exposure: offer at no cost after training within ten working days of initial assignment, unless accepted prior evidence or a medical exception applies. Retain signed refusal when declined. Later acceptance remains available at no cost. Follow-up and clinically directed doses are separate; a complete series or titer is not a universal intake condition.' },
        trigger: { mode: 'conditional', clauses: [{ kind: 'attribute', attribute: 'occupational-blood-exposure', anyOf: ['yes'] }], showBeforeTrigger: true },
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 201'
    },
    {
        key: 'influenza-vaccination-status',
        version: 3,
        label: 'Seasonal flu vaccination status or declining reason',
        legalName: 'Seasonal influenza vaccination status or documented reason for declining',
        citations: ['IC 5.19.1', 'CHAP IPC.5'],
        compliance: { evidenceMode: 'influenza', instructions: 'Record seasonal vaccination status or the actual reason for declining; no signed flu declination is required. Retain orientation education and review status at least annually.' },
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'seasonal',
            seasonStartMonthDay: '10-01',
            seasonEndMonthDay: '03-31',
            reminderTierDays: [
                30,
                14,
                7
            ]
        },
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 202'
    },
    {
        key: 'influenza-program-annual-evaluation',
        label: 'Flu program review',
        legalName: 'Annual Influenza Vaccination Program Evaluation',
        citations: ['IC 5.19.1', 'CHAP IPC.5'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.5 · line 203'
    },
    {
        key: 'occupational-exposure-incident-record',
        label: 'Exposure incident record',
        legalName: 'Bloodborne or communicable disease exposure incident and post-exposure follow-up',
        citations: ['CHAP IPC.16', 'IC 5.12.1', 'IC 5.12D.10', 'OSHA 1910.1030'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'segregated-health',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'attribute',
                    attribute: 'had-exposure-event',
                    anyOf: ['yes']
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.5 · line 204'
    },
    {
        key: 'fitness-for-duty-return-to-work',
        label: 'Return-to-work clearance',
        legalName: 'Return-to-work clearance after an infectious or reportable communicable disease',
        citations: ['IC 5.9.1', 'IC 5.9A.2', 'IC 5.10.1', 'CHAP IPC.16'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'segregated-health',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'attribute',
                    attribute: 'requires-fitness-for-duty',
                    anyOf: ['yes']
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.5 · line 205'
    },
    {
        key: 'absence-medical-documentation',
        label: 'Doctor\'s note for absence',
        legalName: 'Physician statement and return-to-work release for absences',
        citations: ['HR 4.2.1', 'HR 4.8.1'],
        kind: 'document',
        phase: 'continuous',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'segregated-health',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'attribute',
                    attribute: 'requires-fitness-for-duty',
                    anyOf: ['yes']
                }
            ],
            showBeforeTrigger: false
        },
        catalogRow: '§1.5 · line 206'
    },
    {
        key: 'other-agency-health-requirements',
        label: 'Other health requirements',
        legalName: 'Any other Agency-required health requirement',
        citations: ['HR 4.8.1', 'HR 4.9.1', 'CHAP HRM.8'],
        kind: 'document',
        phase: 'both',
        owner: 'person',
        expiry: {
            mode: 'unresolved',
            openQuestionId: 'Q15'
        },
        fileCategory: 'segregated-health',
        catalogRow: '§1.5 · line 207'
    },
    {
        key: 'visit-signature-policy-attestation',
        label: 'Visit signature policy signed',
        legalName: 'Visit Signature and Payroll Verification Policy attestation',
        citations: ['HR 4.26A.2 (rev. July 2026)', 'VIGEO Clinician Policy Attestation Form'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'event-driven'
        },
        fileCategory: 'training-competency',
        catalogRow: '§1.6 · line 213'
    },
    {
        key: 'evv-policy-acknowledgment',
        label: 'EVV policy acknowledgment',
        legalName: 'Electronic Visit Verification policy acknowledgment',
        citations: ['HR 4.26.1', 'HR 4.25.1', 'CoP 484.80(i)', '21st Century Cures Act'],
        kind: 'document',
        phase: 'onboarding',
        owner: 'person',
        expiry: {
            mode: 'none'
        },
        fileCategory: 'training-competency',
        trigger: {
            mode: 'conditional',
            clauses: [
                {
                    kind: 'attribute',
                    attribute: 'delivers-medicaid-services',
                    anyOf: ['yes']
                }
            ],
            showBeforeTrigger: false,
            explanation: 'Medicaid service delivery only (catalog Q44).'
        },
        catalogRow: '§1.6 · line 214'
    },
    {
        key: 'wage-scale-annual-review',
        label: 'Wage scale review',
        legalName: 'Annual Governing Body review of wages and salary scales',
        citations: ['HR 4.22.1', 'HR 4.5.1', 'HR 4.23.1', 'CHAP HRM.1'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'interval',
            everyDays: 365,
            anchor: 'last-completion',
            graceDays: 0,
            reminderTierDays: [
                60,
                30,
                14,
                7,
                1
            ]
        },
        countsTowardProgress: false,
        fileCategory: 'employee-records',
        catalogRow: '§1.6 · line 215'
    },
    {
        key: 'retention-program-elements',
        label: 'Retention program',
        legalName: 'Staff retention program elements documented',
        citations: ['HR 4.23.1', 'CHAP HRM.1', 'TJC HR.01.02.01', 'ACHC HH4-2I.01'],
        kind: 'document',
        phase: 'continuous',
        owner: 'agency',
        expiry: {
            mode: 'unresolved',
            openQuestionId: 'Q63'
        },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.6 · line 216'
    },
    {
        key: 'student-supervision-by-agency-staff',
        version: 2,
        label: 'Student supervision',
        legalName: 'Agency staff supervise students providing patient care',
        citations: ['HR 4.18.1', 'TJC HR.01.02.07', 'CoP 484.75(b)'],
        kind: 'recurring-event',
        phase: 'continuous',
        owner: 'agency',
        expiry: { mode: 'event-driven' },
        countsTowardProgress: false,
        fileCategory: 'training-competency',
        catalogRow: '§1.6 · line 220'
    },
];
//# sourceMappingURL=requirements.seed.js.map